class GiftDrawer extends Drawer {
  connectedCallback() {
    super.connectedCallback();
    this.productsContainer = this.querySelector('#giftDrawerProducts');
    this.currentTier = null;
    this.currentProducts = [];
    this.isSwapping = false;
    this.swapLineIndex = null;
  }
  
  open(triggeredBy, options = {}) {
    this.currentTier = options.tier;
    this.currentProducts = options.products || [];
    this.isSwapping = options.isSwapping || false;
    this.swapLineIndex = options.swapLineIndex || null;
    
    // Update drawer title with threshold
    const threshold = options.threshold || '75';
    const titleElement = this.querySelector('.drawer__heading');
    if (titleElement) {
      const title = this.isSwapping ? 
        `Swap Your Free Gift` : 
        `Free Gift When You Spend £${threshold}`;
      titleElement.textContent = title;
    }
    
    this.populateProducts();
    super.open(triggeredBy);
  }
  
  async populateProducts() {
    if (!this.productsContainer || !this.currentProducts.length) return;
    
    // Get current cart to check for existing gifts
    const cart = await this.getCurrentCart();
    
    let productsHtml = '';
    
    this.currentProducts.forEach((product, index) => {
      productsHtml += this.generateProductHTML(product, index, cart);
    });
    
    this.productsContainer.innerHTML = productsHtml;
    this.addEventListeners();
  }

  getGiftItemByProductId(cart, productId, tier) {
    if (!cart || !cart.items) return null;
    
    return cart.items.find(item => 
      item.properties && 
      item.properties._gift === 'true' && 
      item.properties._gift_tier === tier &&
      item.product_id == productId
    );
  }
  
  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }
  
  generateProductHTML(product, index, cart = null) {
    // Check if THIS SPECIFIC PRODUCT is already in cart for this tier
    const existingGift = cart ? this.getGiftItemByProductId(cart, product.id, this.currentTier) : null;
    const isThisProductInCart = !!existingGift;
    
    // Generate variant options HTML
    let variantsHtml = '';
    let colorOption = null;
    let sizeOption = null;
    
    if (product.variants && product.variants.length > 1 && product.options.length > 0) {
      // Find color and size options
      colorOption = product.options.find(opt => 
        opt.name.toLowerCase().includes('color') || opt.name.toLowerCase().includes('colour')
      );
      sizeOption = product.options.find(opt => 
        opt.name.toLowerCase().includes('size')
      );
      
      // Generate color swatches
      if (colorOption) {
        const colorSwatches = colorOption.values.map((color, colorIndex) => {
          const colorClass = colorIndex === 0 ? 'gift-drawer__variant-option--selected' : '';
          const colorValue = this.getColorValue(color);
          const disabledClass = isThisProductInCart ? 'gift-drawer__variant-option--disabled' : '';
          
          return `<button class="gift-drawer__variant-option gift-drawer__variant-option--color ${colorClass} ${disabledClass}" 
                    data-option-name="${colorOption.name}"
                    data-option-value="${color}"
                    style="--color: ${colorValue};" 
                    data-color="${color}"
                    ${isThisProductInCart ? 'disabled' : ''}></button>`;
        }).join('');
        
        variantsHtml += `
          <div class="gift-drawer__variant-group">
            <label class="gift-drawer__variant-label">Color:</label>
            <div class="gift-drawer__color-options">${colorSwatches}</div>
          </div>
        `;
      }
      
      // Generate size dropdown
      if (sizeOption) {
        const sizeOptions = sizeOption.values.map((size, sizeIndex) => {
          const selected = sizeIndex === 0 ? 'selected' : '';
          return `<option value="${size}" ${selected}>${size}</option>`;
        }).join('');
        
        variantsHtml += `
          <div class="gift-drawer__variant-group">
            <label class="gift-drawer__variant-label">Size:</label>
            <select class="gift-drawer__variant-select gift-drawer__size-select" 
                    data-option-name="${sizeOption.name}"
                    ${isThisProductInCart ? 'disabled' : ''}>
              ${sizeOptions}
            </select>
          </div>
        `;
      }
    }
    
    // Determine button text and state based on whether THIS PRODUCT is in cart
    let buttonText, buttonDisabled, buttonClass;
    
    if (isThisProductInCart) {
      buttonText = 'Add to cart';
      buttonDisabled = 'disabled';
      buttonClass = 'gift-drawer__add-to-cart gift-drawer__add-to-cart--disabled';
    } else {
      buttonText = 'Add to cart';
      buttonDisabled = '';
      buttonClass = 'gift-drawer__add-to-cart';
    }
    
    const defaultVariant = this.getVariantFromOptions(product, {
      [colorOption?.name]: colorOption?.values[0],
      [sizeOption?.name]: sizeOption?.values[0]
    });
    
    return `
      <product-card class="card-wrapper product-card-wrapper gift-card-wrapper gift-drawer__product" data-product-id="${product.id}">
        <div class="card card--standard card--media ${isThisProductInCart ? 'gift-drawer__product--selected' : ''}">
          <div class="card__inner">
            <div class="card__media">
              <a class="media media--transparent" href="${product.url}" aria-label="${product.title}">
                <img src="${product.featured_image}" 
                     alt="${product.featured_image_alt || product.title}"
                     class="motion-reduce"
                     loading="lazy"
                     width="533" height="533">
              </a>
            </div>
          </div>
          
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h4">
                <a href="${product.url}" class="full-unstyled-link card__product-title">
                  ${product.title}
                </a>
              </h3>
              
              <div class="card-information">
                <div class="price gift-drawer__product-price">
                  <span class="price__regular">
                    <span class="visually-hidden">Regular price</span>
                    <span class="price-item price-item--regular gift-drawer__product-price-free">Free</span>
                  </span>
                </div>
                
                ${variantsHtml ? `<div class="gift-drawer__product-variants">${variantsHtml}</div>` : ''}
              </div>

              <button class="${buttonClass}" 
                      data-variant-id="${defaultVariant?.id || product.variants[0].id}" 
                      data-product-title="${product.title}"
                      data-product-id="${product.id}"
                      ${buttonDisabled}>
                ${buttonText}
              </button>
            </div>
          </div>
        </div>
      </product-card>
    `;
  }
  
  getVariantFromOptions(product, selectedOptions) {
    if (!product.variants || !selectedOptions) return null;
    
    return product.variants.find(variant => {
      return Object.keys(selectedOptions).every(optionName => {
        if (!selectedOptions[optionName]) return true;
        
        const optionIndex = product.options.findIndex(opt => opt.name === optionName);
        if (optionIndex === -1) return true;
        
        const variantOptionValue = variant[`option${optionIndex + 1}`];
        return variantOptionValue === selectedOptions[optionName];
      });
    });
  }
  
  getColorValue(colorName) {
    // Map color names to actual color values
    const colorMap = {
      'black': '#000000',
      'white': '#ffffff',
      'navy': '#000080',
      'red': '#ff0000',
      'blue': '#0000ff',
      'green': '#008000',
      'yellow': '#ffff00',
      'pink': '#ffc0cb',
      'purple': '#800080',
      'orange': '#ffa500',
      'brown': '#a52a2a',
      'grey': '#808080',
      'gray': '#808080'
    };
    
    return colorMap[colorName.toLowerCase()] || '#f5f5f5';
  }
  
  addEventListeners() {
    // Color variant selection
    this.productsContainer.querySelectorAll('.gift-drawer__variant-option--color').forEach(btn => {
      btn.addEventListener('click', (e) => this.selectColorVariant(e));
    });
    
    // Size variant selection
    this.productsContainer.querySelectorAll('.gift-drawer__size-select').forEach(select => {
      select.addEventListener('change', (e) => this.selectSizeVariant(e));
    });
    
    // Add to cart buttons
    this.productsContainer.querySelectorAll('.gift-drawer__add-to-cart').forEach(btn => {
      btn.addEventListener('click', (e) => this.addToCart(e));
    });
  }
  
  selectColorVariant(e) {
    const button = e.currentTarget;
    const productContainer = button.closest('.gift-drawer__product');
    
    // Remove selected from color siblings
    productContainer.querySelectorAll('.gift-drawer__variant-option--color').forEach(opt => {
      opt.classList.remove('gift-drawer__variant-option--selected');
    });
    
    // Add selected to clicked color option
    button.classList.add('gift-drawer__variant-option--selected');
    
    // Update variant based on current selections
    this.updateSelectedVariant(productContainer);
  }
  
  selectSizeVariant(e) {
    const select = e.currentTarget;
    const productContainer = select.closest('.gift-drawer__product');
    
    // Update variant based on current selections
    this.updateSelectedVariant(productContainer);
  }
  
  updateSelectedVariant(productContainer) {
    const productId = productContainer.dataset.productId;
    const product = this.currentProducts.find(p => p.id == productId);

    if (!product) return;
    
    // Get current selections
    const selectedColor = productContainer.querySelector('.gift-drawer__variant-option--color.gift-drawer__variant-option--selected')?.dataset.optionValue;
    const selectedSize = productContainer.querySelector('.gift-drawer__size-select')?.value;

    // Build selected options object
    const selectedOptions = {};
    if (selectedColor) {
      const colorOptionName = productContainer.querySelector('.gift-drawer__variant-option--color')?.dataset.optionName;
      if (colorOptionName) selectedOptions[colorOptionName] = selectedColor;
    }
    if (selectedSize) {
      const sizeOptionName = productContainer.querySelector('.gift-drawer__size-select')?.dataset.optionName;
      if (sizeOptionName) selectedOptions[sizeOptionName] = selectedSize;
    }
    
    // Find matching variant
    const matchingVariant = this.getVariantFromOptions(product, selectedOptions);
    
    // Update add to cart button
    const addToCartBtn = productContainer.querySelector('.gift-drawer__add-to-cart');
    if (matchingVariant && addToCartBtn) {
      addToCartBtn.dataset.variantId = matchingVariant.id;
      
      // Update availability if needed
      if (!matchingVariant.available) {
        addToCartBtn.textContent = 'Sold Out';
        addToCartBtn.disabled = true;
      } else {
        const buttonText = this.isSwapping ? 'Swap to this gift' : 'Add to cart';
        addToCartBtn.textContent = buttonText;
        addToCartBtn.disabled = false;
      }
    }
  }
  
  async addToCart(e) {
    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    // const discountCode = button.dataset.discountCode;
    
    if (!variantId) return;
    
    const originalText = button.textContent;
    button.textContent = this.isSwapping ? 'Swapping...' : 'Adding...';
    button.disabled = true;
    
    try {
      // If swapping, remove the old gift first
      if (this.isSwapping && this.swapLineIndex) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: this.swapLineIndex,
            quantity: 0
          })
        });
      }
      
      // Add the new gift
      const cartData = {
        id: variantId,
        quantity: 1,
        properties: {
          '_gift': 'true',
          '_gift_tier': this.currentTier
        }
      };
      
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });
      
      if (!addResponse.ok) {
        throw new Error('Failed to add gift to cart');
      }
      
      const addResult = await addResponse.json();
      
      // Close gift drawer and open cart drawer
      this.close();
      
      // Update cart gifts visibility and open cart drawer
      setTimeout(() => {
        const cartGifts = document.querySelector('cart-gifts');
        if (cartGifts && typeof cartGifts.updateGiftCardsVisibility === 'function') {
          cartGifts.updateGiftCardsVisibility();
        }
        this.openCartDrawer();
      }, 800);
      
    } catch (error) {
      console.error('Error adding/swapping gift:', error);
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  async openCartDrawer() {
    try {
      // Method 1: Try to refresh cart drawer contents first, then open
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Try to fetch fresh cart data and refresh the drawer
        try {
          const response = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`);
          if (response.ok) {
            const responseText = await response.text();
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const buttonToRemove = html.querySelector('.btn-continue-shipping');
            buttonToRemove?.remove();
            const newContent = html.querySelector('.drawer__inner');
            const currentContent = cartDrawer.querySelector('.drawer__inner');

            if (newContent && currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
            }
          }
        } catch (error) {
          console.error('Could not refresh cart drawer content:', error);
        }
        
        // Now open the drawer
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }
      }
      
      // Method 2: Try CartItems refresh approach
      const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
      if (cartItems && typeof cartItems.onCartUpdate === 'function') {
        await cartItems.onCartUpdate();
        
        // Then open cart drawer
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }
      }
      
      // Method 3: Look for cart drawer trigger button
      const cartTrigger = document.querySelector('[data-cart-drawer-trigger], .cart-drawer-trigger, .js-cart-drawer-trigger');
      if (cartTrigger) {
        cartTrigger.click();
        return;
      }
      
      // Method 4: Look for generic cart trigger  
      const cartButton = document.querySelector('.cart-icon, [aria-controls*="cart"], [data-cart-trigger]');
      if (cartButton) {
        cartButton.click();
        return;
      }
      
      // Method 5: Dispatch events
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:open'));
      
    } catch (error) {
      console.error('Error opening cart drawer:', error);
      // Fallback: refresh the page if we can't open the cart drawer properly
      window.location.reload();
    }
  }
}

customElements.define('gift-drawer', GiftDrawer);
